const toStringOrNull = (value) => {
  if (value == null) return null;
  try {
    const normalized = String(value).trim();
    return normalized || null;
  } catch {
    return null;
  }
};

export const parseSyncEpoch = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const resolveGroupRuntimeHandle = (group = {}) =>
  toStringOrNull(
    group?.runtimeHandle ??
      group?.runtime_handle ??
      group?.groupHandle ??
      group?.group_handle ??
      null,
  );

export const getPendingWelcomesCount = (group = {}) => {
  const pending = group?.pendingWelcomes ?? group?.pending_welcomes ?? null;
  return Array.isArray(pending) ? pending.length : 0;
};

export const hasPersistedWelcomePayload = (group = {}) =>
  Boolean(
    toStringOrNull(
      group?.recoveryWelcomeB64 ??
        group?.recovery_welcome_b64 ??
        group?.welcomeB64 ??
        group?.welcome_b64 ??
        group?.welcome ??
        null,
    ),
  );

export const hasJoinedGroupHistory = (group = {}) => {
  if (group?.joined === true) {
    return true;
  }

  const runtimeEpoch = parseSyncEpoch(
    group?.runtimeEpoch ?? group?.runtime_epoch ?? group?.runtime_epoch_no ?? null,
  );
  if (runtimeEpoch != null && runtimeEpoch > 0) {
    return true;
  }

  // A consumed key-package ref only proves that a Welcome targeted this device.
  // It must not be treated as successful local join history, otherwise pending
  // welcomes can be suppressed before the runtime group actually materializes.
  return false;
};

export const getNormalizedMembers = (group = {}) => {
  const source = group?.members ?? group?.memberIds ?? group?.member_ids ?? [];
  if (!Array.isArray(source)) {
    return [];
  }
  return source
    .map((entry) => toStringOrNull(entry))
    .filter(Boolean);
};

export const getIdentityCandidates = (group = {}, options = {}) => {
  const candidates = new Set();
  const push = (value) => {
    const normalized = toStringOrNull(value);
    if (normalized) {
      candidates.add(normalized);
    }
  };

  const explicitIdentityIds = Array.isArray(options?.identityIds) ? options.identityIds : [];
  explicitIdentityIds.forEach(push);

  push(options?.identityId);
  push(group?.activeIdentityId);
  push(group?.active_identity_id);
  push(group?.currentIdentityId);
  push(group?.current_identity_id);
  push(group?.identityClientId);
  push(group?.identity_client_id);
  push(group?.clientId);
  push(group?.client_id);
  push(group?.identityId);
  push(group?.identity_id);

  return Array.from(candidates);
};

export const hasCurrentIdentityMembership = (group = {}, options = {}) => {
  const members = getNormalizedMembers(group);
  if (!members.length) {
    return false;
  }

  const candidates = getIdentityCandidates(group, options);
  if (!candidates.length) {
    return false;
  }

  return candidates.some((candidate) => members.includes(candidate));
};

export const isRuntimeBehindSnapshot = (group = {}) => {
  const snapshotEpoch = parseSyncEpoch(
    group?.targetSnapshotEpoch ??
      group?.epoch ??
      group?.current_epoch ??
      group?.currentEpoch ??
      null,
  );
  const runtimeEpoch = parseSyncEpoch(
    group?.runtimeEpoch ?? group?.runtime_epoch ?? group?.runtime_epoch_no ?? null,
  );
  return snapshotEpoch != null && runtimeEpoch != null && runtimeEpoch < snapshotEpoch;
};

export const deriveCanonicalSyncAction = (canonicalState) => {
  switch (canonicalState) {
    case 'aligned':
      return 'none';
    case 'needs_recovery':
      return 'recover';
    case 'needs_welcome':
      return 'consume_welcome';
    case 'joining':
      return 'wait_for_runtime';
    default:
      return 'fetch_snapshot';
  }
};

export const deriveGroupSyncState = (group = {}, options = {}) => {
  const snapshotEpoch = parseSyncEpoch(
    group?.targetSnapshotEpoch ??
      group?.epoch ??
      group?.current_epoch ??
      group?.currentEpoch ??
      null,
  );
  const runtimeEpoch = parseSyncEpoch(
    group?.runtimeEpoch ?? group?.runtime_epoch ?? group?.runtime_epoch_no ?? null,
  );
  const runtimeHandle = resolveGroupRuntimeHandle(group);
  const pendingWelcomesCount = getPendingWelcomesCount(group);
  const needsRecovery = Boolean(group?.needsRecovery);
  const ready = options?.ready === true;
  const pendingSetup = Boolean(group?.pending_setup ?? group?.pendingSetup);
  const currentIdentityMember =
    options?.currentIdentityMember === true || hasCurrentIdentityMembership(group, options);
  const unresolvedCurrentMemberBootstrap =
    currentIdentityMember &&
    !hasJoinedGroupHistory(group) &&
    (pendingSetup || pendingWelcomesCount > 0 || hasPersistedWelcomePayload(group));
  const welcomeRequired = Boolean(
    group?.welcomeRequired ??
      group?.welcome_required ??
      options?.welcomeRequired ??
      ((!currentIdentityMember && (pendingSetup || getNormalizedMembers(group).length > 0)) ||
        unresolvedCurrentMemberBootstrap),
  );
  const runtimeAvailable = Boolean(runtimeHandle) && runtimeEpoch != null && runtimeEpoch > 0;
  const epochAligned =
    snapshotEpoch != null &&
    runtimeEpoch != null &&
    runtimeEpoch >= snapshotEpoch &&
    runtimeAvailable;

  let runtimeState = 'missing';
  if (ready) {
    runtimeState = 'ready';
  } else if (needsRecovery) {
    runtimeState = 'recovery_required';
  } else if (welcomeRequired) {
    runtimeState = 'welcome_required';
  } else if (!runtimeAvailable) {
    runtimeState = 'runtime_missing';
  } else if (snapshotEpoch != null && runtimeEpoch < snapshotEpoch) {
    runtimeState = 'runtime_behind';
  } else {
    runtimeState = 'not_ready';
  }

  let syncStatus = 'idle';
  if (ready) {
    syncStatus = 'ready';
  } else if (needsRecovery) {
    syncStatus = 'recovering';
  } else if (welcomeRequired) {
    syncStatus = 'joining';
  } else if (snapshotEpoch == null || snapshotEpoch <= 0) {
    syncStatus = runtimeAvailable ? 'bootstrapping' : 'idle';
  } else if (!runtimeAvailable) {
    syncStatus = pendingWelcomesCount > 0 ? 'joining' : 'bootstrapping';
  } else if (runtimeEpoch < snapshotEpoch) {
    syncStatus = 'catching_up';
  } else if (pendingWelcomesCount > 0) {
    syncStatus = 'joining';
  } else {
    syncStatus = 'bootstrapping';
  }

  let canonicalState = 'missing';
  if (ready) {
    canonicalState = 'aligned';
  } else if (needsRecovery) {
    canonicalState = 'needs_recovery';
  } else if (welcomeRequired) {
    canonicalState = 'needs_welcome';
  } else if (
    pendingSetup ||
    pendingWelcomesCount > 0 ||
    runtimeAvailable ||
    (snapshotEpoch != null && snapshotEpoch > 0 && currentIdentityMember)
  ) {
    canonicalState = 'joining';
  }

  const syncAction = deriveCanonicalSyncAction(canonicalState);
  const isStable = canonicalState === 'aligned';
  const isTerminal = canonicalState === 'aligned' || canonicalState === 'missing';

  return {
    targetSnapshotEpoch: snapshotEpoch,
    runtimeEpoch,
    runtimeHandle,
    pendingWelcomesCount,
    pendingSetup,
    currentIdentityMember,
    welcomeRequired,
    epochAligned,
    runtimeState,
    syncStatus,
    canonicalState,
    syncAction,
    isStable,
    isTerminal,
  };
};

const normalizeDerivedState = (groupOrState = {}, options = {}) => {
  if (
    groupOrState &&
    typeof groupOrState === 'object' &&
    typeof groupOrState.canonicalState === 'string'
  ) {
    return groupOrState;
  }
  return deriveGroupSyncState(groupOrState, options);
};

export const getCanonicalGroupSyncState = (groupOrState = {}, options = {}) =>
  normalizeDerivedState(groupOrState, options).canonicalState;

export const isGroupSyncStable = (groupOrState = {}, options = {}) =>
  getCanonicalGroupSyncState(groupOrState, options) === 'aligned';

export const requiresRuntimeBootstrap = (groupOrState = {}, options = {}) => {
  const canonicalState = getCanonicalGroupSyncState(groupOrState, options);
  return (
    canonicalState === 'joining' ||
    canonicalState === 'needs_welcome' ||
    canonicalState === 'needs_recovery'
  );
};
